import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { Chart as ChartJS, registerables } from "chart.js";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlined from "@mui/icons-material/LockOutlined";
import {
  ArrowDropDown,
  ArrowDropUp,
  FileDownloadOutlined,
  FilterAlt,
  OpenInNewOutlined,
  FilterAltOutlined,
  InfoOutlined,
  Insights,
  ShowChart,
} from "@mui/icons-material";
import { useGlobalContext } from "../../context/global";
import {
  buildCompareRangesFromSelection,
  getTodayCalendarDate,
} from "../../constant/calendarDefaultConfigurations";
import Header from "../../components/header/header";
import { formatDate } from "../../components/Reports/Components/utils";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import ComplexFilterWidget from "../../components/Filter/ComplexFilterWidget";
import { commonFilterOperators } from "../../components/Filter/FilterData/quadrantFilter";
import { mergeCustomOutputToAPIFiltersFormat } from "../../helper/Quadrant/quadrantHelper";
import useUserDetails from "../../helper/userData/useUserDetails";
import { openRequestTrackingPage } from "../../helper/common/openRequestTrackingPage";
import { requestBackendReportExport } from "../../helper/PageWiseExportLogic/backendReportExport";
import ExportDownloadTooltip, {
  EXPORT_LIMIT_TOOLTIP_MESSAGE,
  EXPORT_TRACK_TOOLTIP_MESSAGE,
  getExportRedirectIconButtonSx,
} from "../../components/Reports/Components/ExportDownloadTooltip";
import TrendDialog from "./TrendDialog";
import CategoryTrendDialog from "./CategoryTrendDialog";
import ProductTrackingFilters, { EMPTY_QUICK_FILTERS, countQuickFilters, quickFilterRuleItems } from "./ProductTrackingFilters";
import {
  PRODUCT_TRACKING_ASIN_DETAIL_MEASURES,
  PRODUCT_TRACKING_ASIN_HIDDEN_METRICS,
  PRODUCT_TRACKING_DEFAULT_ORDER,
  PRODUCT_TRACKING_EXPORT_DETAIL_FIELDS,
  PRODUCT_TRACKING_EXPORT_REPORT_TYPE,
  PRODUCT_TRACKING_LOCKED_MESSAGE,
  PRODUCT_TRACKING_PAGE_SIZE,
  PRODUCT_TRACKING_QUICK_FILTERS,
  PRODUCT_TRACKING_STATUS_LABELS,
  PRODUCT_TRACKING_TREND_DIMENSIONS,
  PRODUCT_TYPE_LABELS,
  isProductTrackingEnabled,
  productTrackingConfigFor,
} from "../../constant/productTracking/productTrackingConfig";

// For the trend dialogs' charts.
ChartJS.register(...registerables);

const PRIMARY = "#6759FF";

const formatValue = (value, format, decimals = 2) => {
  if (value === undefined || value === null || value === "") return "-";
  if (format === "status") return PRODUCT_TRACKING_STATUS_LABELS[value] ?? value;
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  switch (format) {
    case "text":
      return value;
    case "deals":
      return `${Math.round(num).toLocaleString("en-IN")} deals`;
    case "currency":
      return `₹ ${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
    case "percent":
      return `${num.toLocaleString("en-IN", { maximumFractionDigits: decimals })}%`;
    case "integer":
      return Math.round(num).toLocaleString("en-IN");
    default:
      return num.toLocaleString("en-IN", { maximumFractionDigits: decimals });
  }
};

const NUMERIC_FORMATS = ["currency", "percent", "decimal", "integer"];
// Columns with a Current / Previous pair in comparison view (sizes: previous period's size chips).
const COMPARABLE_FORMATS = [...NUMERIC_FORMATS, "sizes"];
const COMPARISON_SUFFIX = "_comparison";
const CHANGE_PERCENTAGE_SUFFIX = "_change_percentage";
// reportsapi returns INT_MAX as the change when the previous value is 0.
const INFINITE_CHANGE = 2147483647;

const ChangePercent = ({ value, lowerIsBetter }) => {
  if (value === undefined || value === null || value === "" || Number.isNaN(Number(value))) return null;
  const num = Number(value);
  if (num === 0) return <Typography sx={{ fontSize: "0.7rem", color: "#8C9196", lineHeight: 1.2 }}>(0%)</Typography>;
  const good = lowerIsBetter ? num < 0 : num > 0;
  const text = Math.abs(num) >= INFINITE_CHANGE ? "∞" : `${num > 0 ? "▲" : "▼"} ${Math.abs(num).toFixed(0)}%`;
  return <Typography sx={{ fontSize: "0.7rem", color: good ? "#388e3c" : "#d32f2f", lineHeight: 1.2 }}>{text}</Typography>;
};

// Percentage columns (discount, in-stock rate) change by points, not by a percent of a percent:
// 30% -> 40% shows ▲ 10. Every other column keeps the % change from reportsapi.
const Change = ({ format, current, previous, changePercentage, lowerIsBetter }) => {
  if (format !== "percent") return <ChangePercent value={changePercentage} lowerIsBetter={lowerIsBetter} />;
  const isNum = (v) => v !== undefined && v !== null && v !== "" && !Number.isNaN(Number(v));
  if (!isNum(current) || !isNum(previous)) return null;
  const diff = Math.round((Number(current) - Number(previous)) * 10) / 10;
  if (diff === 0) return <Typography sx={{ fontSize: "0.7rem", color: "#8C9196", lineHeight: 1.2 }}>(0)</Typography>;
  const good = lowerIsBetter ? diff < 0 : diff > 0;
  return (
    <Typography sx={{ fontSize: "0.7rem", color: good ? "#388e3c" : "#d32f2f", lineHeight: 1.2 }}>
      {`${diff > 0 ? "▲" : "▼"} ${Math.abs(diff).toLocaleString("en-IN", { maximumFractionDigits: 1 })}`}
    </Typography>
  );
};

const defaultOrder = (analysisBy) => PRODUCT_TRACKING_DEFAULT_ORDER[analysisBy] ?? ["productCount", "desc"];

// sizes is the JSON array from the scrape: [{size, color, child_product_id, in_stock}].
const parseSizes = (value) => {
  try {
    const sizes = JSON.parse(value || "[]");
    return Array.isArray(sizes) ? sizes : [];
  } catch (e) {
    return [];
  }
};

// Info icon next to the LQS value: the checks the listing failed (lqsFailedChecks, a JSON list).
const LqsFailedInfo = ({ value }) => {
  let failed;
  try {
    failed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!Array.isArray(failed)) return null;
  const title = failed.length ? (
    <Box>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, mb: 0.5 }}>Not passed</Typography>
      {failed.map((check) => (
        <Typography key={check} sx={{ fontSize: "0.75rem" }}>• {check}</Typography>
      ))}
    </Box>
  ) : (
    "All checks passed"
  );
  return (
    <Tooltip title={title} arrow placement="top">
      <InfoOutlined aria-label="LQS checks not passed" sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle", color: "#8C9196" }} />
    </Tooltip>
  );
};

const SizeChips = ({ value }) => {
  const sizes = parseSizes(value);
  if (!sizes.length) return "-";
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end", maxWidth: 220, ml: "auto" }}>
      {sizes.map((s, i) => (
        <Box
          key={`${s.size}-${i}`}
          title={s.in_stock ? "In stock" : "Out of stock"}
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: "4px",
            fontSize: "0.7rem",
            lineHeight: 1.4,
            border: "1px solid",
            borderColor: s.in_stock ? "#A7E3C4" : "#E1E3E5",
            background: s.in_stock ? "#E8F8F0" : "#F6F6F7",
            color: s.in_stock ? "#0B7A47" : "#8C9196",
            textDecoration: s.in_stock ? "none" : "line-through",
          }}
        >
          {s.size}
        </Box>
      ))}
    </Box>
  );
};

const ruleItem = (member, operator, values) => ({ type: "RULE_ITEM", member, operator, values });

const buildDimensionFilters = (items) =>
  items.length ? { dimensions: { type: "AND_RULE", and: items } } : undefined;

const advancedFilterConfig = (fields) => ({
  measure: { operators: commonFilterOperators, fields: fields.measure },
  dimension: { operators: commonFilterOperators, fields: fields.dimension },
  campaigns: { operators: commonFilterOperators, fields: [] },
  topXConfig: { operators: commonFilterOperators, fields: [] },
});

const countRuleItems = (node) => {
  if (!node || typeof node !== "object") return 0;
  if (node.type === "RULE_ITEM") return 1;
  return [...(node.and ?? []), ...(node.or ?? [])].reduce((n, child) => n + countRuleItems(child), 0);
};

const SEARCH_FILTER_KEYS = PRODUCT_TRACKING_QUICK_FILTERS.filter((f) => f.kind === "search").map((f) => f.key);

// The selected marketplace, named the way the sidebar names it.
const marketplaceOf = (selectedPlatform) =>
  selectedPlatform?.toLowerCase()?.includes("amazon") ? "amazon" : selectedPlatform === "flipkart" ? "flipkart" : "myntra";

const ProductTrackingPage = ({ marketplace }) => {
  const config = useMemo(() => productTrackingConfigFor(marketplace), [marketplace]);
  const advancedConfig = useMemo(() => advancedFilterConfig(config.advancedFilterFields), [config]);
  const { clientId } = useGlobalContext();
  // Page-local calendar (like Request Tracking): opens on today and never changes the global one.
  const [calendarDate, setCalendarDate] = useState(() => getTodayCalendarDate());
  const [multiCalendarDate, setMultiCalendarDate] = useState(() =>
    buildCompareRangesFromSelection(getTodayCalendarDate()[0])
  );
  const handleLocalCalendarChange = useCallback(
    ({ calendarDate: nextCalendarDate, multiCalendarDate: nextMultiCalendarDate }) => {
      if (nextCalendarDate) setCalendarDate(nextCalendarDate);
      if (nextMultiCalendarDate) setMultiCalendarDate(nextMultiCalendarDate);
    },
    []
  );
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );

  const [analysisBy, setAnalysisBy] = useState(config.analysisOptions[0].value);
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [quickFilters, setQuickFilters] = useState(EMPTY_QUICK_FILTERS);
  // ComplexFilterWidget's own format ({metrics, dimension, ...} with custom_output rule trees).
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterPanelMode, setFilterPanelMode] = useState("quick");
  const [order, setOrder] = useState(defaultOrder(config.analysisOptions[0].value));
  const [currentPage, setCurrentPage] = useState(1);
  const [isComparisonView, setIsComparisonView] = useState(
    () => new URLSearchParams(window.location.search).get("isComparisonView") === "true"
  );
  const [trendRow, setTrendRow] = useState(null);
  const [isCategoryTrendOpen, setIsCategoryTrendOpen] = useState(false);
  const [dynamicFilterOptions, setDynamicFilterOptions] = useState({});

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isExportRequested, setIsExportRequested] = useState(false);
  const [userDetails] = useUserDetails();

  // Comparison view: current period = selection1, previous = selection2 of the header's compare calendar.
  const startDate = formatDate(isComparisonView ? multiCalendarDate.selection1.startDate : calendarDate[0].startDate);
  const endDate = formatDate(isComparisonView ? multiCalendarDate.selection1.endDate : calendarDate[0].endDate);
  const previousStartDate = formatDate(multiCalendarDate.selection2.startDate);
  const previousEndDate = formatDate(multiCalendarDate.selection2.endDate);
  const analysisOption = config.analysisOptions.find((o) => o.value === analysisBy);
  const isAsinView = analysisBy === "productId";
  const isDateView = analysisBy === "date";
  const metricKeys = config.metricColumns.map((c) => c.key);
  const tableColumns = isAsinView
    ? [
        ...config.asinColumns,
        ...config.metricColumns.filter((c) => !PRODUCT_TRACKING_ASIN_HIDDEN_METRICS.includes(c.key)),
      ]
    : config.metricColumns;
  const url = `${process.env.REACT_APP_REPORT_API}/client/${clientId}/${marketplace}:scrape-insights`;
  const comparisonUrl = `${url}-comparison`;
  // Comparison needs a dimension to join on, so it is off for "Analysis by" Date.
  const isComparing = isComparisonView && !isDateView;
  const isComparable = (col) => isComparing && COMPARABLE_FORMATS.includes(col.format);

  // Advanced filters -> {dimensions, measures} rule trees (same helper as Performance Analysis).
  const advancedApiFilters = useMemo(
    () =>
      mergeCustomOutputToAPIFiltersFormat(
        advancedFilters?.metrics?.custom_output,
        advancedFilters?.dimension?.custom_output,
        null,
        null
      ),
    [advancedFilters]
  );
  const countFromSummary = isAsinView && !advancedApiFilters.measures;
  const advancedFilterCount = countRuleItems(advancedApiFilters.dimensions) + countRuleItems(advancedApiFilters.measures);

  // Row-level filters (quick filters + advanced attribute rules): used everywhere, trends included.
  const baseFilterItems = useMemo(
    () => [...quickFilterRuleItems(quickFilters), ...(advancedApiFilters.dimensions ? [advancedApiFilters.dimensions] : [])],
    [quickFilters, advancedApiFilters]
  );

  const activeFilterItems = useMemo(() => {
    const items = [...baseFilterItems];
    // Several values separated by commas, spaces or new lines (e.g. pasted ASINs) match any of them.
    const terms = analysisOption?.searchable ? searchValue.split(/[\s,]+/).filter(Boolean) : [];
    if (terms.length === 1) {
      items.push(ruleItem(analysisBy, "contains", terms));
    } else if (terms.length > 1) {
      items.push({ type: "OR_RULE", or: terms.map((term) => ruleItem(analysisBy, "contains", [term])) });
    }
    return items;
  }, [baseFilterItems, searchValue, analysisBy, analysisOption]);

  const timeDimensions = useMemo(
    () => [{ dimension: "date", dateRange: [startDate, endDate] }],
    [startDate, endDate]
  );

  const postQuery = useCallback(
    async (query, signal, endpoint = url) => {
      const response = await axios.post(endpoint, { query }, { signal });
      return response?.data?.data ?? [];
    },
    [url]
  );

  const comparisonTimeDimensions = useMemo(
    () => [
      { dimension: "date", dateRange: [startDate, endDate] },
      { dimension: "date", dateRange: [previousStartDate, previousEndDate] },
    ],
    [startDate, endDate, previousStartDate, previousEndDate]
  );

  // The calendar's "Compare data" switch.
  const handleComparisonView = useCallback((isComparison) => setIsComparisonView(isComparison), []);

  // Options for the quick filters' search pickers (Brand, Seller, Category): values in the date range.
  useEffect(() => {
    if (!clientId) return;
    const controller = new AbortController();
    SEARCH_FILTER_KEYS.forEach((key) => {
      postQuery(
        { dimensions: [key], measures: ["productCount"], timeDimensions, order: [[key, "asc"]], limit: 500 },
        controller.signal
      )
        .then((data) => {
          const delimiter = PRODUCT_TRACKING_QUICK_FILTERS.find((f) => f.key === key)?.listDelimiter;
          const values = data.map((r) => r[key]).filter((v) => v !== undefined && v !== null && v !== "");
          const options = delimiter ? [...new Set(values.flatMap((v) => v.split(delimiter)).filter(Boolean))] : values;
          setDynamicFilterOptions((prev) => ({ ...prev, [key]: options }));
        })
        .catch(() => {});
    });
    return () => controller.abort();
  }, [clientId, timeDimensions, postQuery]);

  // Table + summary
  useEffect(() => {
    if (!clientId) return;
    const controller = new AbortController();
    const dimensionFilters = buildDimensionFilters(activeFilterItems);
    // Metric (advanced) filters pick table rows; the summary stays the total of the row filters.
    const filters = advancedApiFilters.measures ? { ...dimensionFilters, measures: advancedApiFilters.measures } : dimensionFilters;
    const queryTimeDimensions = isComparing ? comparisonTimeDimensions : timeDimensions;
    const endpoint = isComparing ? comparisonUrl : url;
    const tableQuery = {
      dimensions: [analysisBy],
      measures: [...tableColumns.map((c) => c.key), ...(isAsinView ? PRODUCT_TRACKING_ASIN_DETAIL_MEASURES : [])],
      timeDimensions: queryTimeDimensions,
      filters,
      order: [order],
      limit: PRODUCT_TRACKING_PAGE_SIZE,
      offset: (currentPage - 1) * PRODUCT_TRACKING_PAGE_SIZE,
    };
    const summaryQuery = { dimensions: [], measures: metricKeys, timeDimensions: queryTimeDimensions, filters: dimensionFilters };

    setIsLoading(true);
    setError("");
    Promise.all([postQuery(tableQuery, controller.signal, endpoint), postQuery(summaryQuery, controller.signal, endpoint)])
      .then(([tableRows, summaryRows]) => {
        setRows(tableRows.slice(0, PRODUCT_TRACKING_PAGE_SIZE));
        setSummary(summaryRows[0] ?? null);
        if (countFromSummary) setTotalRows(summaryRows[0]?.productCount ?? 0);
        setIsLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel?.(err)) return;
        setRows([]);
        setSummary(null);
        setError(err?.response?.data?.message || "Could not load data");
        setIsLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, analysisBy, activeFilterItems, advancedApiFilters, timeDimensions, comparisonTimeDimensions, isComparing, order, currentPage]);

  // Total rows of the table, for the page count and "of N": the same rows as the table, current period
  // only, with one light measure. Not re-run on page or sort change. Not needed in the product view
  // without metric filters: there one row is one product, so the summary's productCount is the total.
  useEffect(() => {
    if (!clientId || countFromSummary) return;
    const controller = new AbortController();
    const dimensionFilters = buildDimensionFilters(activeFilterItems);
    const filters = advancedApiFilters.measures ? { ...dimensionFilters, measures: advancedApiFilters.measures } : dimensionFilters;
    const filterMeasures = [];
    const collect = (node) => {
      if (!node) return;
      if (node.type === "RULE_ITEM") filterMeasures.push(node.member);
      [...(node.and ?? []), ...(node.or ?? [])].forEach(collect);
    };
    collect(advancedApiFilters.measures);
    postQuery(
      {
        dimensions: [analysisBy],
        measures: [...new Set(["productCount", ...filterMeasures])],
        timeDimensions,
        filters,
      },
      controller.signal
    )
      .then((data) => setTotalRows(data.length))
      .catch((err) => {
        if (axios.isCancel?.(err)) return;
        setTotalRows(0);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, analysisBy, activeFilterItems, advancedApiFilters, timeDimensions, countFromSummary]);

  // A new query is a new export: the button goes back from "open Request Tracking" to "download".
  useEffect(() => {
    setIsExportRequested(false);
  }, [analysisBy, activeFilterItems, advancedApiFilters, timeDimensions, comparisonTimeDimensions, isComparing, order]);

  useEffect(() => {
    setCurrentPage(1);
  }, [analysisBy, activeFilterItems, advancedApiFilters, timeDimensions, comparisonTimeDimensions, isComparing, order]);

  const handleSort = (key) => {
    setOrder(([currentKey, dir]) => [key, currentKey === key && dir === "desc" ? "asc" : "desc"]);
  };

  // Download runs on the backend (adyogiadsapi report export, report_type PRODUCT_TRACKING), like
  // Product Analytics: it pages through every row of the current table query, not just this page, and
  // the file is picked up from Request Tracking. A second click opens Request Tracking.
  const handleDownload = async () => {
    if (isExportRequested) {
      openRequestTrackingPage();
      return;
    }
    if (!totalRows) {
      toast.info("No records available for export.");
      return;
    }
    const dimensionFilters = buildDimensionFilters(activeFilterItems);
    const filters = advancedApiFilters.measures ? { ...dimensionFilters, measures: advancedApiFilters.measures } : dimensionFilters;
    const detailFields = isAsinView ? PRODUCT_TRACKING_EXPORT_DETAIL_FIELDS : [];
    const columnKeys = tableColumns.map((c) => c.key);
    setIsExporting(true);
    try {
      await requestBackendReportExport({
        clientId,
        userPlatform: marketplace,
        reportType: PRODUCT_TRACKING_EXPORT_REPORT_TYPE,
        totalCount: totalRows,
        triggeredBy: userDetails?.user?.email ?? localStorage.getItem("email") ?? "",
        queryBody: {
          query: {
            dimensions: [analysisBy],
            measures: [...columnKeys, ...detailFields],
            timeDimensions: isComparing ? comparisonTimeDimensions : timeDimensions,
            filters,
            order: [order],
          },
        },
        frontendConfig: {
          is_comparison: isComparing,
          include_summary_row: false,
          columns: [analysisBy, ...detailFields, ...columnKeys],
        },
      });
      setIsExportRequested(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not start the export");
    } finally {
      setIsExporting(false);
    }
  };

  const appliedFilterCount = countQuickFilters(quickFilters) + advancedFilterCount;

  const hasTrend = PRODUCT_TRACKING_TREND_DIMENSIONS.includes(analysisBy);

  const renderTrendButton = (row) => (
    <IconButton
      size="small"
      title="See trend"
      onClick={() => setTrendRow(row)}
      sx={{ ml: "auto", alignSelf: "flex-end", p: 0.25, color: PRIMARY, flexShrink: 0 }}
    >
      <ShowChart sx={{ fontSize: 18 }} />
    </IconButton>
  );

  const renderDimensionCell = (row) => {
    const value = row[analysisBy];
    if (isAsinView) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 52,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#F6F6F7",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            {row.imageUrl ? (
              <img src={row.imageUrl} alt="" loading="lazy" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            ) : null}
          </Box>
          <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 500,
              color: "#202223",
              maxWidth: 280,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.title}
          >
            {row.title || "-"}
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "#8C9196" }}>
            {config.idLabel}:{" "}
            <a href={config.productUrl(value)} target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY }}>
              {value}
            </a>
          </Typography>
          </Box>
          {renderTrendButton(row)}
        </Box>
      );
    }
    if (analysisBy === "productType") return PRODUCT_TYPE_LABELS[value] ?? value ?? "-";
    if (analysisBy === "availability" || analysisBy === "listingStatus") return formatValue(value, "status");
    if (hasTrend && value !== undefined && value !== null) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>{value}</span>
          {renderTrendButton(row)}
        </Box>
      );
    }
    return value ?? "-";
  };

  // Comparison header: row 1 = metric (spans current + previous), row 2 = Current / Previous.
  const HEADER_ROW_HEIGHT = 48;
  // Row 2 sticks right under row 1, so it needs row 1's rendered height (padding + sort label make
  // it taller than HEADER_ROW_HEIGHT); a fixed offset leaves it misaligned with the rowSpan cells.
  const firstHeadRowRef = useRef(null);
  const [firstHeadRowHeight, setFirstHeadRowHeight] = useState(HEADER_ROW_HEIGHT);
  useLayoutEffect(() => {
    const row = firstHeadRowRef.current;
    if (!isComparing || !row) return undefined;
    const measure = () => setFirstHeadRowHeight(row.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [isComparing]);
  const periodLabel = (from, to) => `${from} to ${to}`;

  const headCellSx = {
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "#00000099",
    whiteSpace: "nowrap",
    borderRight: "1px solid #ddd",
    background: "#ffffff",
    padding: "14px 16px",
  };
  const stickyFirstColumnSx = { position: "sticky", left: 0, zIndex: 3, minWidth: 320, background: "#ffffff" };

  return (
    <div
      className={
        dashboardSideBarVisibiltyState ? "main-content-container-onopen-mode" : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Product Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Daily price, discount, stock, rating and delivery of your {config.name} products.
          </Typography>
        </Box>
      </Box>

      {/* Filter Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 2, alignItems: "center" }}>
        <Autocomplete
          options={config.analysisOptions}
          disableClearable
          getOptionLabel={(option) => option.label}
          value={analysisOption}
          onChange={(event, selectedOption) => {
            if (!selectedOption) return;
            setAnalysisBy(selectedOption.value);
            setOrder(defaultOrder(selectedOption.value));
            setInputValue("");
            setSearchValue("");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Analysis by"
              variant="outlined"
              size="small"
              sx={{
                "& .MuiInputLabel-root": { fontSize: "14px", color: "#333333" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#333333" },
                "& .MuiInputBase-root": { height: "36px", fontSize: "12px !important", color: "#333333" },
                "& .MuiInputBase-input": { fontSize: "12px !important", color: "#333333", padding: "6px 8px" },
              }}
            />
          )}
          size="small"
          sx={{ minWidth: "220px", "& .MuiSvgIcon-root": { color: "#333333" } }}
          slotProps={{
            listbox: {
              sx: {
                fontSize: "12px",
                "& .MuiAutocomplete-option": {
                  fontSize: "12px",
                  color: "#333333",
                  padding: "6px 12px",
                  "&:hover": { backgroundColor: "#EDEDED" },
                },
              },
            },
          }}
        />

        {/* Search Box */}
        <TextField
          size="small"
          placeholder={
            analysisOption?.searchable
              ? `Search ${analysisOption.label} (separate several with commas)`
              : `Search is not available for ${analysisOption?.label}`
          }
          variant="outlined"
          value={inputValue}
          disabled={!analysisOption?.searchable}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSearchValue(e.target.value.trim());
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "16px", color: "#333333" }} />
              </InputAdornment>
            ),
            endAdornment: searchValue && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={() => {
                    setSearchValue("");
                    setInputValue("");
                  }}
                >
                  <CloseIcon sx={{ fontSize: "16px", color: "#333333" }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            "& .MuiInputBase-root": { height: "36px", fontSize: "12px", color: "#333333" },
            "& .MuiInputBase-input": { fontSize: "12px", color: "#333333", padding: "6px 4px" },
            "& .MuiInputBase-input::placeholder": { fontSize: "12px", color: "#000000", opacity: 0.6 },
          }}
        />

        <Header
          showOnlyCalendar={true}
          includeToday={true}
          localCalendar={true}
          onLocalCalendarChange={handleLocalCalendarChange}
          handleComparisonView={handleComparisonView}
          hideComparisonSwitch={isDateView}
        />

        {/* Filters */}
        <ToggleButton
          value="filters"
          selected={isFilterPanelOpen}
          onChange={() => setIsFilterPanelOpen((v) => !v)}
          sx={{
            height: "36px",
            borderRadius: 1,
            px: 1.5,
            borderColor: "grey.400",
            backgroundColor: "white",
            color: "#333333",
            textTransform: "none",
            "& .MuiTypography-root": { fontSize: "12px", color: "#333333" },
            "& svg": { color: "#333333" },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Badge
              overlap="circular"
              variant="dot"
              invisible={!appliedFilterCount}
              sx={{ "& .MuiBadge-badge": { bgcolor: "primary.main" } }}
            >
              {isFilterPanelOpen ? <FilterAlt fontSize="small" /> : <FilterAltOutlined fontSize="small" />}
            </Badge>
            <Typography fontWeight={500}>Filters</Typography>
            {isFilterPanelOpen ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
          </Stack>
        </ToggleButton>

        {/* Download (backend export; then a link to Request Tracking) */}
        <ExportDownloadTooltip
          autoOpen={isExportRequested}
          title={isExportRequested ? EXPORT_TRACK_TOOLTIP_MESSAGE : EXPORT_LIMIT_TOOLTIP_MESSAGE}
        >
          <span>
            <IconButton
              onClick={handleDownload}
              disabled={isExporting || (!isExportRequested && !totalRows)}
              sx={getExportRedirectIconButtonSx(isExportRequested)}
            >
              {isExporting ? (
                <CircularProgress size={18} thickness={5} sx={{ color: "grey.600" }} />
              ) : isExportRequested ? (
                <OpenInNewOutlined sx={{ fontSize: 24 }} />
              ) : (
                <FileDownloadOutlined sx={{ fontSize: 24 }} />
              )}
            </IconButton>
          </span>
        </ExportDownloadTooltip>
      </Box>

      <ProductTrackingFilters
        open={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        mode={filterPanelMode}
        onModeChange={setFilterPanelMode}
        applied={quickFilters}
        onApply={(next) => {
          setQuickFilters(next);
          setIsFilterPanelOpen(false);
        }}
        optionsByKey={dynamicFilterOptions}
        advancedCount={advancedFilterCount}
        onClearAdvanced={() => setAdvancedFilters({})}
        advancedWidget={
          <ComplexFilterWidget
            key="product_tracking_page"
            config={advancedConfig}
            campaignFilter={false}
            showDimensionFilter={true}
            filters={advancedFilters}
            showPopupModal={isFilterPanelOpen}
            setShowPopupModal={setIsFilterPanelOpen}
            output={(filters) => {
              setAdvancedFilters(filters);
              setIsFilterPanelOpen(false);
            }}
            prefixMember=""
            type="page"
            sidebar={true}
            default="metrics"
            page="overlay"
            isAppliedCallback={() => {}}
            summary="always"
            summaryOutput={() => {}}
            calloutTitle="Select your filters"
            calloutContent="+ Add Filter"
            hideFilterSummary={true}
          />
        }
      />

      {error && (
        <Typography color="error" sx={{ fontSize: "13px", mb: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden", position: "relative" }}>
          {isLoading && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)", zIndex: 10 }}>
              <CircularProgress size={28} sx={{ color: PRIMARY }} />
            </Box>
          )}
          <TableContainer sx={{ maxHeight: 600, minHeight: 400 }}>
            <Table stickyHeader size="small" sx={{ borderCollapse: "separate" }}>
              <TableHead>
                <TableRow ref={firstHeadRowRef}>
                  <TableCell
                    rowSpan={isComparing ? 2 : 1}
                    sx={{ ...headCellSx, ...stickyFirstColumnSx, zIndex: 6 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                      <TableSortLabel
                        active={order[0] === analysisBy}
                        direction={order[0] === analysisBy ? order[1] : "asc"}
                        onClick={() => handleSort(analysisBy)}
                      >
                        {analysisOption?.label}
                      </TableSortLabel>
                      <Tooltip title="Category trends" arrow>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Insights sx={{ fontSize: 16 }} />}
                          onClick={() => setIsCategoryTrendOpen(true)}
                          sx={{ textTransform: "none", fontSize: "11px", py: 0.25, px: 1, minWidth: 0, borderColor: "#B9B2FF", color: PRIMARY, background: "#FBFAFF" }}
                        >
                          Category trends
                        </Button>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  {tableColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={isComparable(col) ? "center" : "right"}
                      colSpan={isComparable(col) ? 2 : 1}
                      rowSpan={isComparing && !isComparable(col) ? 2 : 1}
                      sx={{
                        ...headCellSx,
                        minWidth: 120,
                        ...(isComparing ? { height: HEADER_ROW_HEIGHT, boxSizing: "border-box" } : {}),
                        background: order[0] === col.key ? "#F7F6FE" : "#ffffff",
                      }}
                    >
                      <Tooltip title={col.tooltip ?? ""} arrow placement="top">
                        <span>
                          {col.sortable === false ? (
                            col.label
                          ) : (
                            <TableSortLabel
                              active={order[0] === col.key}
                              direction={order[0] === col.key ? order[1] : "desc"}
                              onClick={() => handleSort(col.key)}
                            >
                              {col.label}
                            </TableSortLabel>
                          )}
                        </span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
                {isComparing && (
                  <TableRow>
                    {tableColumns.filter(isComparable).map((col) =>
                      [
                        ["Current", periodLabel(startDate, endDate)],
                        ["Previous", periodLabel(previousStartDate, previousEndDate)],
                      ].map(([label, range]) => (
                        <TableCell
                          key={`${col.key}-${label}`}
                          align="right"
                          title={range}
                          sx={{ ...headCellSx, top: `${firstHeadRowHeight}px`, padding: "8px 16px", fontWeight: 500, minWidth: 100 }}
                        >
                          {label}
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {summary && (
                  <TableRow sx={{ background: "#F6F6F7" }}>
                    <TableCell sx={{ ...stickyFirstColumnSx, background: "#F6F6F7", fontWeight: 600, fontSize: "0.8rem", borderRight: "1px solid #ddd", padding: "14px 16px" }}>
                      Summary
                    </TableCell>
                    {tableColumns.map((col) => {
                      const key = col.key in summary ? col.key : col.summaryKey;
                      const format = key === col.key ? col.format : col.summaryFormat ?? col.format;
                      const cellSx = { fontWeight: 600, fontSize: "0.8rem", borderRight: "1px solid #ddd", background: order[0] === col.key ? "#EEEBFF" : "#F6F6F7" };
                      return [
                        <TableCell key={col.key} align="right" sx={cellSx}>
                          {key ? formatValue(summary[key], format, col.decimals) : ""}
                          {isComparable(col) && key ? (
                            <Change
                              format={format}
                              current={summary[key]}
                              previous={summary[key + COMPARISON_SUFFIX]}
                              changePercentage={summary[key + CHANGE_PERCENTAGE_SUFFIX]}
                              lowerIsBetter={col.lowerIsBetter}
                            />
                          ) : null}
                        </TableCell>,
                        isComparable(col) ? (
                          <TableCell key={`${col.key}-previous`} align="right" sx={{ ...cellSx, background: "#F6F6F7" }}>
                            {key ? formatValue(summary[key + COMPARISON_SUFFIX], format, col.decimals) : ""}
                          </TableCell>
                        ) : null,
                      ];
                    })}
                  </TableRow>
                )}
                {rows.map((row, idx) => (
                  <TableRow key={`${row[analysisBy] ?? "none"}-${idx}`} hover>
                    <TableCell sx={{ ...stickyFirstColumnSx, fontSize: "0.8rem", borderRight: "1px solid #ddd", padding: "12px 16px" }}>
                      {renderDimensionCell(row)}
                    </TableCell>
                    {tableColumns.map((col) => [
                      <TableCell
                        key={col.key}
                        align="right"
                        sx={{ fontSize: "0.8rem", borderRight: "1px solid #ddd", background: order[0] === col.key ? "#F7F6FE" : "inherit" }}
                      >
                        {col.format === "sizes" ? <SizeChips value={row[col.key]} /> : formatValue(row[col.key], col.format, col.decimals)}
                        {col.key === "lqs" && row.lqs != null ? <LqsFailedInfo value={row.lqsFailedChecks} /> : null}
                        {isComparable(col) && col.format !== "sizes" ? (
                          <Change
                            format={col.format}
                            current={row[col.key]}
                            previous={row[col.key + COMPARISON_SUFFIX]}
                            changePercentage={row[col.key + CHANGE_PERCENTAGE_SUFFIX]}
                            lowerIsBetter={col.lowerIsBetter}
                          />
                        ) : null}
                      </TableCell>,
                      isComparable(col) ? (
                        <TableCell key={`${col.key}-previous`} align="right" sx={{ fontSize: "0.8rem", borderRight: "1px solid #ddd", color: "#6D7175" }}>
                          {col.format === "sizes" ? (
                            <SizeChips value={row[col.key + COMPARISON_SUFFIX]} />
                          ) : (
                            formatValue(row[col.key + COMPARISON_SUFFIX], col.format, col.decimals)
                          )}
                        </TableCell>
                      ) : null,
                    ])}
                  </TableRow>
                ))}
                {!isLoading && !rows.length && (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + tableColumns.filter(isComparable).length + 1} align="center" sx={{ py: 8, color: "#8C9196" }}>
                      No scraped data for this date range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ borderTop: "1px solid #ddd", py: 1 }}>
            <CustomPagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalRows / PRODUCT_TRACKING_PAGE_SIZE)}
              totalItems={totalRows}
              onPageChange={setCurrentPage}
              itemsPerPage={PRODUCT_TRACKING_PAGE_SIZE}
            />
          </Box>
        </Box>

      <TrendDialog
        open={Boolean(trendRow)}
        onClose={() => setTrendRow(null)}
        dimension={analysisBy}
        value={trendRow?.[analysisBy]}
        title={isAsinView ? trendRow?.title || trendRow?.[analysisBy] : trendRow?.[analysisBy]}
        subtitle={
          isAsinView
            ? `${config.idLabel}: ${trendRow?.[analysisBy] ?? ""}${trendRow?.productType ? ` · ${PRODUCT_TYPE_LABELS[trendRow.productType] ?? trendRow.productType}` : ""}`
            : analysisOption?.label
        }
        imageUrl={isAsinView ? trendRow?.imageUrl : null}
        metrics={config.trendMetrics}
        dateRange={[startDate, endDate]}
        baseFilterItems={baseFilterItems}
        postQuery={postQuery}
        formatValue={formatValue}
      />

      <CategoryTrendDialog
        open={isCategoryTrendOpen}
        metrics={config.trendMetrics}
        onClose={() => setIsCategoryTrendOpen(false)}
        dateRange={[startDate, endDate]}
        baseFilterItems={baseFilterItems}
        postQuery={postQuery}
        formatValue={formatValue}
      />
    </div>
  );
};

// Keyed by marketplace so switching it starts the page fresh (its filters and columns are per marketplace).
const ProductTracking = () => {
  const { selectedPlatform, clientId } = useGlobalContext();
  const marketplace = marketplaceOf(selectedPlatform);
  if (clientId && !isProductTrackingEnabled(clientId)) {
    return (
      <Box sx={{ p: 4, display: "flex", alignItems: "center", gap: 1, color: "#8C9196" }}>
        <LockOutlined fontSize="small" />
        <Typography sx={{ fontSize: "14px" }}>{PRODUCT_TRACKING_LOCKED_MESSAGE}</Typography>
      </Box>
    );
  }
  return <ProductTrackingPage key={marketplace} marketplace={marketplace} />;
};

export default ProductTracking;
